import React, { FC, ReactNode } from 'react';
import { Spin } from 'antd';
import moment from 'moment-timezone';
import { BookableDay, formatLongDate, formatSlotTime } from '../../utils/clinician-availability';
import { DATE_WITH_DASH } from '../../constants/time-formats';
import { formatTimezone } from '../../utils/timezone';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
export const MONTH_KEY = 'YYYY-MM';
// How far ahead a patient can page the calendar.
const MAX_MONTHS_AHEAD = 12;

type Props = {
  month: string; // YYYY-MM
  // eslint-disable-next-line no-unused-vars
  onMonthChange: (month: string) => void;
  days: BookableDay[]; // bookable days in `month`
  isLoading: boolean;
  date?: string;
  startTime?: string;
  // eslint-disable-next-line no-unused-vars
  onSelectDate: (date: string) => void;
  // eslint-disable-next-line no-unused-vars
  onSelectTime: (startTime: string) => void;
  timezone: string;
  // Shown above the times, e.g. the timezone picker, so the clock is chosen before the time.
  header?: ReactNode;
};

// Months a patient can page through: this month to MAX_MONTHS_AHEAD ahead, on their clock.
export const getMonthBounds = (timezone: string) => ({
  firstMonth: moment.tz(timezone).format(MONTH_KEY),
  lastMonth: moment.tz(timezone).add(MAX_MONTHS_AHEAD, 'months').format(MONTH_KEY),
});

type SlotGridProps = {
  day: BookableDay;
  startTime?: string;
  // eslint-disable-next-line no-unused-vars
  onSelectTime: (startTime: string) => void;
};

export const SlotGrid: FC<SlotGridProps> = ({ day, startTime, onSelectTime }) => (
  <div className="slots">
    {day.slots.map(({ start }) => (
      <button
        key={start}
        type="button"
        className={start === startTime ? 'on' : ''}
        aria-pressed={start === startTime}
        onClick={() => onSelectTime(start)}
      >
        {formatSlotTime(start)}
      </button>
    ))}
  </div>
);

// The mockup's calendar (left) and times (right).
const TimePicker: FC<Props> = ({
  month, onMonthChange, days, isLoading, date, startTime, onSelectDate, onSelectTime, timezone, header,
}) => {
  const { firstMonth, lastMonth } = getMonthBounds(timezone);
  const monthStart = moment(month, MONTH_KEY);
  const available = new Set(days.map((d) => d.date));
  const cells = Array.from({ length: monthStart.daysInMonth() }, (_, i) => monthStart.clone().date(i + 1).format(DATE_WITH_DASH));
  const selectedDay = days.find((d) => d.date === date);

  const shiftMonth = (offset: number) => onMonthChange(monthStart.clone().add(offset, 'month').format(MONTH_KEY));

  const renderTimes = () => {
    if (selectedDay) {
      return (
        <>
          <p className="fhint" style={{ marginBottom: 10 }}>
            {'Times for '}
            <b>{formatLongDate(selectedDay.date)}</b>
            {` — ${formatTimezone(timezone, selectedDay.date)}`}
          </p>
          <SlotGrid day={selectedDay} startTime={startTime} onSelectTime={onSelectTime} />
        </>
      );
    }
    if (!isLoading && !days.length) {
      return <div className="empty">{`No appointments in ${monthStart.format('MMMM')}. Try the next month.`}</div>;
    }
    return <div className="empty">Pick a highlighted date to see available times.</div>;
  };

  return (
    <div className="calwrap">
      <div className="cal">
        <div className="calhd">
          <button type="button" aria-label="Previous month" disabled={month <= firstMonth} onClick={() => shiftMonth(-1)}>‹</button>
          <b>{monthStart.format('MMMM YYYY')}</b>
          <button type="button" aria-label="Next month" disabled={month >= lastMonth} onClick={() => shiftMonth(1)}>›</button>
        </div>
        <Spin spinning={isLoading}>
          <div className="calg">
            {WEEKDAYS.map((d) => <div key={d} className="dow">{d}</div>)}
            {Array.from({ length: monthStart.day() }, (_, i) => <button key={`pad-${i}`} type="button" disabled aria-hidden tabIndex={-1} />)}
            {cells.map((cell) => {
              const isAvailable = available.has(cell);
              return (
                <button
                  key={cell}
                  type="button"
                  className={`${isAvailable ? 'avail' : ''}${cell === date ? ' on' : ''}`}
                  disabled={!isAvailable}
                  aria-pressed={cell === date}
                  aria-label={`${formatLongDate(cell)}${isAvailable ? ', available' : ', no appointments'}`}
                  onClick={() => onSelectDate(cell)}
                >
                  {Number(cell.slice(8))}
                </button>
              );
            })}
          </div>
        </Spin>
        <div className="callegend" aria-hidden>
          <span>
            <i style={{ background: 'var(--primary-light)' }} />
            Available
          </span>
          <span>
            <i style={{ background: 'var(--primary)' }} />
            Selected
          </span>
          <span>
            <i style={{ background: '#F0F2F7' }} />
            No appointments
          </span>
        </div>
      </div>
      <div>
        {header}
        {renderTimes()}
      </div>
    </div>
  );
};

export default TimePicker;
